import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

export const fetchSliders = createAsyncThunk(
  "slider/fetchSliders",
  async (all = false, { rejectWithValue }) => {
    try {
      const url = all ? "/slider?all=true" : "/slider";
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch sliders");
    }
  }
);

export const createSlider = createAsyncThunk(
  "slider/createSlider",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post("/slider", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to create slide");
    }
  }
);

export const updateSlider = createAsyncThunk(
  "slider/updateSlider",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/slider/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update slide");
    }
  }
);

export const deleteSlider = createAsyncThunk(
  "slider/deleteSlider",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/slider/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete slide");
    }
  }
);

export const reorderSliders = createAsyncThunk(
  "slider/reorderSliders",
  async (items, { rejectWithValue }) => {
    try {
      const response = await api.put("/slider/reorder", { items });
      return items;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to reorder");
    }
  }
);

const initialState = {
  sliders: [],
  loading: false,
  error: null,
  saving: false,
};

const sliderSlice = createSlice({
  name: "slider",
  initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSliders.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchSliders.fulfilled, (state, action) => {
        state.loading = false;
        state.sliders = action.payload.data.sliders;
      })
      .addCase(fetchSliders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createSlider.pending, (state) => { state.saving = true; state.error = null; })
      .addCase(createSlider.fulfilled, (state, action) => {
        state.saving = false;
        state.sliders.push(action.payload.data.slider);
        state.sliders.sort((a, b) => a.order - b.order);
      })
      .addCase(createSlider.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      .addCase(updateSlider.pending, (state) => { state.saving = true; state.error = null; })
      .addCase(updateSlider.fulfilled, (state, action) => {
        state.saving = false;
        const updated = action.payload.data.slider;
        const idx = state.sliders.findIndex((s) => s._id === updated._id);
        if (idx !== -1) state.sliders[idx] = updated;
        state.sliders.sort((a, b) => a.order - b.order);
      })
      .addCase(updateSlider.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      .addCase(deleteSlider.pending, (state) => { state.saving = true; })
      .addCase(deleteSlider.fulfilled, (state, action) => {
        state.saving = false;
        state.sliders = state.sliders.filter((s) => s._id !== action.payload);
      })
      .addCase(deleteSlider.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      .addCase(reorderSliders.fulfilled, (state, action) => {
        action.payload.forEach(({ id, order }) => {
          const s = state.sliders.find((x) => x._id === id);
          if (s) s.order = order;
        });
        state.sliders.sort((a, b) => a.order - b.order);
      });
  },
});

export const { clearError } = sliderSlice.actions;
export default sliderSlice.reducer;
